import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { parseEntityRef } from '@backstage/catalog-model';
import { InfoCardVariants } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Alert } from '@material-ui/lab';

import { ArgoCDLogoSmall } from '../../../home/ArgoCDLogo';
import { ContentSection } from './ContentSection';
import { ProblemTooltip } from './CreationStatusPanel';
import { InfoTooltip } from './InfoTooltip';
import { InlineField } from './InlineField';
import { SectionItem } from './SectionItem';
import { StatusIndicator } from './StatusIndicator';
import { TablePager } from './TablePager';
import {
  ChildEntityStatus,
  useProjectArgocdStatus,
  useProjectChildEntities,
  useChildEntityStatuses,
} from './hooks/useArgocdStatus';
import { useStyles } from './styles';
import {
  ANNOTATION_PROJECT_NAME,
  compactCellStyle,
  compactHeaderStyle,
  deriveOverallHealth,
  deriveOverallSync,
  formatTimestamp,
  statusIndicatorType,
  SUPPORT_URL,
} from './utils';

export interface ArgocdProjectStatusCardProps {
  variant?: InfoCardVariants;
}

const StatusCell = ({
  status,
  message,
}: {
  status?: string;
  message?: string;
}) => {
  if (!status) {
    return (
      <Typography variant="body2" color="textSecondary">
        —
      </Typography>
    );
  }
  return (
    <Box display="flex" alignItems="center">
      <StatusIndicator type={statusIndicatorType(status)} label={status} />
      <InfoTooltip message={message} />
    </Box>
  );
};

const ROWS_PER_PAGE = 5;

const StatusTable = ({ entries }: { entries: ChildEntityStatus[] }) => {
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [entries.length]);

  const paginatedEntries = useMemo(
    () => entries.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE),
    [entries, page],
  );

  return (
    <Box mt={1} mb={2}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell style={compactHeaderStyle}>Name</TableCell>
            <TableCell style={compactHeaderStyle}>Creation</TableCell>
            <TableCell style={compactHeaderStyle}>Health</TableCell>
            <TableCell style={compactHeaderStyle}>Sync</TableCell>
            <TableCell style={compactHeaderStyle}>Last Sync</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedEntries.map(entry => (
            <TableRow key={entry.entityRef}>
              <TableCell style={compactCellStyle}>
                <Box display="flex" alignItems="center" gridGap={6}>
                  <Tooltip title="Open in Frontstage" arrow>
                    <Link
                      href={`/catalog/default/component/${
                        parseEntityRef(entry.entityRef).name
                      }`}
                      style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                    >
                      <Typography variant="body2">
                        {entry.displayName}
                      </Typography>
                      <OpenInNewIcon style={{ fontSize: 14, opacity: 0.6 }} />
                    </Link>
                  </Tooltip>
                  {entry.argocdUrl && (
                    <Tooltip title="Open in ArgoCD" arrow>
                      <Link
                        href={entry.argocdUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Open in ArgoCD"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          opacity: 0.9,
                        }}
                      >
                        <ArgoCDLogoSmall />
                      </Link>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell style={compactCellStyle}>
                <StatusCell
                  status={entry.creationStatus}
                  message={entry.errorMessage}
                />
              </TableCell>
              <TableCell style={compactCellStyle}>
                <StatusCell
                  status={entry.healthStatus}
                  message={entry.healthStatusMessage}
                />
              </TableCell>
              <TableCell style={compactCellStyle}>
                <StatusCell status={entry.syncStatus} />
              </TableCell>
              <TableCell style={compactCellStyle}>
                <Tooltip
                  title={
                    entry.lastSync
                      ? DateTime.fromISO(entry.lastSync).toLocaleString(
                          DateTime.DATETIME_FULL,
                        )
                      : ''
                  }
                  arrow
                >
                  <Typography variant="body2">
                    {formatTimestamp(entry.lastSync)}
                  </Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePager
        page={page}
        setPage={setPage}
        total={entries.length}
        rowsPerPage={ROWS_PER_PAGE}
      />
    </Box>
  );
};

export const ArgocdProjectStatusCard = ({
  variant,
}: ArgocdProjectStatusCardProps) => {
  const { entity } = useEntity();
  const classes = useStyles();

  const projectName =
    entity.metadata.annotations?.[ANNOTATION_PROJECT_NAME] ||
    entity.metadata.name;

  const {
    data: projectData,
    loading: projectLoading,
    error: projectError,
    refresh: refreshProject,
  } = useProjectArgocdStatus(projectName);

  const {
    apps: childApps,
    appsets: childAppsets,
    loading: entitiesLoading,
    refresh: refreshEntities,
  } = useProjectChildEntities(projectName);

  const [refreshKey, setRefreshKey] = useState(0);
  const { statuses, loading: statusesLoading } = useChildEntityStatuses(
    childApps,
    childAppsets,
    projectName,
    refreshKey,
  );

  const appStatuses = useMemo(
    () => statuses.filter(s => s.type === 'application'),
    [statuses],
  );
  const appsetStatuses = useMemo(
    () => statuses.filter(s => s.type === 'applicationset'),
    [statuses],
  );

  const overallHealth = useMemo(
    () => deriveOverallHealth(statuses),
    [statuses],
  );
  const overallSync = useMemo(() => deriveOverallSync(statuses), [statuses]);
  const degradedProblems = useMemo(
    () =>
      statuses
        .filter(
          e => e.creationStatus === 'Failed' || e.healthStatus === 'Degraded',
        )
        .map(e => {
          const issues = [
            e.creationStatus === 'Failed' && 'Creation Failed',
            e.healthStatus === 'Degraded' && 'Degraded',
          ]
            .filter(Boolean)
            .join(', ');
          return { name: e.displayName, reason: issues };
        }),
    [statuses],
  );
  const outOfSyncProblems = useMemo(
    () =>
      statuses
        .filter(e => e.syncStatus === 'OutOfSync')
        .map(e => ({ name: e.displayName, reason: 'OutOfSync' })),
    [statuses],
  );

  const loading = projectLoading || entitiesLoading || statusesLoading;
  const hasData = projectData && !entitiesLoading;

  const defaultTab =
    hasData && appStatuses.length === 0 && appsetStatuses.length > 0 ? 1 : 0;
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const currentTab = activeTab ?? defaultTab;

  const refresh = useCallback(() => {
    refreshProject();
    refreshEntities();
    setRefreshKey(k => k + 1);
  }, [refreshProject, refreshEntities]);

  const cardClass =
    variant === 'gridItem' ? classes.gridItemCard : classes.fullHeightCard;
  const cardContentClass =
    variant === 'gridItem'
      ? classes.gridItemCardContent
      : classes.fullHeightCardContent;

  return (
    <Card className={cardClass}>
      <CardHeader
        title="ArgoCD Project Status"
        className={classes.cardHeader}
        action={
          <IconButton
            aria-label="Refresh"
            title="Refresh status"
            onClick={refresh}
          >
            <RefreshIcon />
          </IconButton>
        }
      />
      <Divider />
      <CardContent className={cardContentClass}>
        {loading && !hasData && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={32} />
          </Box>
        )}

        {projectError && !projectData && (
          <Alert severity="error">
            Failed to fetch ArgoCD status: {projectError.message}
          </Alert>
        )}

        {hasData && (
          <Box>
            {projectData.creationStatus === 'Pending' && (
              <>
                <ContentSection label="Summary" gridSizes={{ xs: 12 }}>
                  <SectionItem>
                    <InlineField label="Creation">
                      <StatusIndicator type="pending" label="Pending" />
                    </InlineField>
                  </SectionItem>
                </ContentSection>
                <Alert severity="warning" style={{ marginBottom: 16 }}>
                  Provisioning is in progress and may take up to 5 minutes.
                  Should it take longer, please{' '}
                  <Link href={SUPPORT_URL}>contact us</Link>.
                </Alert>
              </>
            )}

            {projectData.creationStatus === 'Failed' && (
              <>
                <ContentSection label="Summary" gridSizes={{ xs: 12 }}>
                  <SectionItem>
                    <InlineField label="Creation">
                      <StatusIndicator type="failed" label="Failed" />
                    </InlineField>
                  </SectionItem>
                </ContentSection>
                <Alert severity="error" style={{ marginBottom: 8 }}>
                  {projectData.creationStatusMessage ||
                    'An error occurred during project creation.'}
                </Alert>
                <Alert severity="info" style={{ marginBottom: 16 }}>
                  If this error is unexpected, see our{' '}
                  <Link href={SUPPORT_URL}>support page</Link> for help.
                </Alert>
              </>
            )}

            {projectData.creationStatus !== 'Pending' && (
              <>
                <ContentSection label="Summary" gridSizes={{ xs: 12 }}>
                  <SectionItem>
                    {projectData.creationStatus === 'Succeeded' &&
                      appStatuses.length === 0 &&
                      appsetStatuses.length === 0 && (
                        <InlineField label="Creation">
                          <StatusIndicator type="succeeded" label="Succeeded" />
                        </InlineField>
                      )}
                    <InlineField label="Status">
                      <Box display="flex" alignItems="center">
                        <StatusIndicator
                          type={overallHealth.type}
                          label={overallHealth.label}
                        />
                        <ProblemTooltip problems={degradedProblems} />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ margin: '0 6px' }}
                        >
                          |
                        </Typography>
                        <StatusIndicator
                          type={overallSync.type}
                          label={overallSync.label}
                        />
                        <ProblemTooltip problems={outOfSyncProblems} />
                        {statuses.length === 0 && (
                          <InfoTooltip message="Shows the combined status of all applications and application sets in this project once they are created." />
                        )}
                      </Box>
                    </InlineField>
                  </SectionItem>
                </ContentSection>

                <Divider style={{ marginTop: -8, marginBottom: 16 }} />

                <Box display="flex" gridGap={24} mb={1}>
                  {[
                    { label: `Applications (${appStatuses.length})`, index: 0 },
                    {
                      label: `Application Sets (${appsetStatuses.length})`,
                      index: 1,
                    },
                  ].map(tab => (
                    <Typography
                      key={tab.index}
                      variant="h2"
                      onClick={() => setActiveTab(tab.index)}
                      style={{
                        textTransform: 'uppercase',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        letterSpacing: 1,
                        whiteSpace: 'nowrap',
                        fontFamily: 'Gilroy',
                        cursor: 'pointer',
                        opacity: currentTab === tab.index ? 1 : 0.4,
                      }}
                    >
                      {tab.label}
                    </Typography>
                  ))}
                </Box>

                <div>
                  {currentTab === 0 &&
                    (appStatuses.length > 0 ? (
                      <StatusTable entries={appStatuses} />
                    ) : (
                      <Box mt={2} mb={3}>
                        <Typography variant="body2" color="textSecondary">
                          No applications found.
                        </Typography>
                      </Box>
                    ))}

                  {currentTab === 1 &&
                    (appsetStatuses.length > 0 ? (
                      <StatusTable entries={appsetStatuses} />
                    ) : (
                      <Box mt={2} mb={3}>
                        <Typography variant="body2" color="textSecondary">
                          No application sets found.
                        </Typography>
                      </Box>
                    ))}
                </div>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
